import type { RealtimeTransport } from './events';
import { parseLockList, parseLockReply, type LockChange, type LockHolder, type LockReply } from './message';

/** The server drops a lock 30s after its last sign of life; holders renew at this interval. */
export const RENEW_MS = 10_000;

/** How long a lock call waits for the channel to be subscribed before it gives up. */
export const SUBSCRIBE_WAIT_MS = 10_000;

/** Who holds which field on one entity, as the server last told us. */
export type LockTable = ReadonlyMap<string, LockHolder>;

/** What a lock call came back with: whether this connection holds the field now, and who holds it. */
export type LockOutcome = { acquired: boolean; holder: LockHolder | null };

/** The lock table of one entity channel, and the calls that change it. */
export type EntityLocks = {
  /** Same map until a lock changes. */
  getTable(): LockTable;
  subscribe(listener: () => void): () => void;
  /**
   * Takes a free field. Resolves with `acquired: false` and the holder when someone else has it.
   * Until `release`, a field that is free again (a failed request, an expired lock) is asked for
   * again on each renewal tick; another holder ends that.
   */
  lock(field: string): Promise<LockOutcome>;
  /** Takes the field whoever holds it. */
  takeOver(field: string): Promise<LockOutcome>;
  /** Frees the field if this connection holds it. Never rejects: a lock that cannot be freed expires. */
  release(field: string): Promise<void>;
  /** Folds in a `lock.changed` from the channel. */
  applyChange(change: LockChange): void;
  /** Merges the server's table (`lock.list`) into ours, field by field, where it is not older than what we have. */
  resync(): Promise<void>;
  /** The channel is subscribed: lock calls can go out, and the table is re-read. */
  onSubscribed(): void;
  /** Frees the locks this session holds; called on the last leave, which also unsubscribes. */
  releaseAll(): Promise<void>;
  /** Stops renewing. */
  close(): void;
};

const sameHolder = (a: LockHolder | null | undefined, b: LockHolder | null) =>
  a === b ||
  (a != null && b !== null && a.client === b.client && a.id === b.id && a.name === b.name && a.tab === b.tab);

const sameTable = (a: LockTable, b: LockTable) =>
  a.size === b.size && [...a].every(([field, holder]) => sameHolder(b.get(field), holder));

function withHolder(table: LockTable, field: string, holder: LockHolder | null): LockTable {
  if (sameHolder(table.get(field), holder)) return table;
  const next = new Map(table);
  if (holder === null) next.delete(field);
  else next.set(field, holder);
  return next;
}

/**
 * Locks on one entity channel. The table is the only state: a lock is held by this connection
 * when its holder is this connection's client id, so a lock lost to a take-over, an expiry or a
 * reconnect stops being renewed as soon as the table says so.
 *
 * The server numbers every real change on the channel (`seq`, +1 each, across all fields), so
 * ordering is tracked per field: `fieldSeq` is the seq each field's state was last taken from, and
 * the last `lock.list` seq stands in for fields not seen since. An event applies only when it is
 * newer than its field's; a list or a call's reply applies when it is at least as new, since it
 * describes the state at its `seq`. A newer change to one field says nothing about another.
 */
export function createEntityLocks(transport: RealtimeTransport, channel: string): EntityLocks {
  let table: LockTable = new Map();
  const listeners = new Set<() => void>();
  const fieldSeq = new Map<string, number>();
  let listSeq = -1;
  const seenFor = (field: string) => fieldSeq.get(field) ?? listSeq;

  const isMine = (holder: LockHolder | undefined) => holder !== undefined && holder.client === transport.clientId();

  // Fields this session asked to lock and has not released. A request that failed (realtime_api or
  // Redis briefly down) or a lock that expired while renewals could not get through is asked for
  // again on the next tick while the field is free. Someone else holding it ends the request, the
  // moment the table shows it: a tab never takes back a field another tab got.
  const wanted = new Set<string>();
  // Every connection id this session has held a lock under. The id changes on each reconnect, and
  // the table can still show a lock under the previous one until it expires; that lock is ours.
  const ownClients = new Set<string>();
  const heldByOther = (holder: LockHolder) => {
    const self = transport.clientId();
    // While disconnected there is no id to compare with, so nobody counts as another holder.
    return self !== null && holder.client !== self && !ownClients.has(holder.client);
  };

  const replace = (next: LockTable) => {
    if (next === table) return;
    table = next;
    for (const field of wanted) {
      const holder = table.get(field);
      if (holder !== undefined && heldByOther(holder)) wanted.delete(field);
    }
    for (const holder of table.values()) if (isMine(holder)) ownClients.add(holder.client);
    listeners.forEach((listener) => listener());
  };
  const setHolder = (field: string, holder: LockHolder | null) => replace(withHolder(table, field, holder));

  /** Says whether a state of `field` at `seq` is current (not older than what we have), and records it. */
  const reaches = (field: string, seq: number) => {
    if (seq < seenFor(field)) return false;
    fieldSeq.set(field, seq);
    return true;
  };

  // The server refuses lock calls without a subscription, and an editor often locks as the file
  // opens, before the channel has subscribed. Calls wait for it rather than fail; once subscribed
  // they go out at once, so a release still reaches the socket in the tick it was made.
  let subscribed = false;
  let markSubscribed = () => {};
  const firstSubscription = new Promise<void>((resolve) => (markSubscribed = resolve));
  function untilSubscribed() {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const giveUp = new Promise<never>((_, reject) => {
      timer = setTimeout(() => reject(new Error(`realtime is not subscribed to ${channel}`)), SUBSCRIBE_WAIT_MS);
    });
    return Promise.race([firstSubscription, giveUp]).finally(() => clearTimeout(timer));
  }

  async function call(method: string, field: string): Promise<LockReply> {
    if (!subscribed) await untilSubscribed();
    const reply = await transport.rpc(method, { channel, field }, parseLockReply);
    if (reply.field !== field) throw new Error(`${method} answered for ${reply.field}, not ${field}`);
    return reply;
  }

  // Acquires still waiting for their answer, so a release made meanwhile can wait for it.
  const pending = new Map<string, Promise<unknown>>();

  async function acquire(method: string, field: string): Promise<LockOutcome> {
    const answer = call(method, field);
    const settled = answer.catch(() => null);
    pending.set(field, settled);
    try {
      const { holder, acquired, seq } = await answer;
      if (holder !== null && heldByOther(holder)) wanted.delete(field);
      if (reaches(field, seq)) setHolder(field, holder);
      return { acquired, holder };
    } finally {
      if (pending.get(field) === settled) pending.delete(field);
    }
  }

  async function release(field: string) {
    wanted.delete(field);
    // Only wait when an acquire is in flight. Otherwise the release must go out now: a session that
    // replaces this one in the same tick (a remount) sends its acquire next, and a release that
    // lands after it would free the new session's lock, since both share this connection.
    const waiting = pending.get(field);
    if (waiting !== undefined) await waiting;
    if (!isMine(table.get(field))) return;
    // Forget it first: a renewal must not land between the call and its answer.
    setHolder(field, null);
    const reply = await call('lock.release', field).catch(() => null);
    if (reply !== null && reaches(field, reply.seq)) setHolder(field, reply.holder);
  }

  async function renew(field: string) {
    const reply = await call('lock.renew', field).catch(() => null);
    // A failed renewal is retried on the next tick; a lost lock stays lost.
    if (reply !== null && isMine(table.get(field)) && reaches(field, reply.seq)) setHolder(field, reply.holder);
  }

  // One `lock.list` at a time: a slow one is not stacked behind by the next tick or a resubscribe.
  let listing: Promise<void> | null = null;

  const renewAll = () => {
    for (const [field, holder] of table) if (isMine(holder)) void renew(field);
    for (const field of wanted) {
      const holder = table.get(field);
      if (holder !== undefined && heldByOther(holder)) wanted.delete(field);
      else if (holder === undefined && !pending.has(field)) void acquire('lock.acquire', field).catch(() => {});
    }
    // Re-read the table on every tick, not only while it shows somebody else's lock. The server says
    // nothing when a TTL runs out, and a `lock.changed` it failed to publish (a Redis restart) leaves
    // the table empty, so an empty table is no proof that the file is unlocked.
    if (listing === null) void resync();
  };
  // Set when a caller asks for a read while one is in flight: that read may predate what the caller
  // needs (a resubscribe), so one more runs after it settles. Ticks skip instead (see renewAll).
  let listAgain = false;
  // centrifuge-js times an rpc out after 5s, so `listing` always settles and cannot block later reads.
  function resync(): Promise<void> {
    if (listing !== null) {
      listAgain = true;
      return listing;
    }
    listing = readList().finally(() => {
      listing = null;
      if (listAgain) {
        listAgain = false;
        void resync();
      }
    });
    return listing;
  }
  async function readList() {
    const list = await transport.rpc('lock.list', { channel }, parseLockList).catch(() => null);
    if (list === null) return;
    const listed = new Map(list.locks.map(({ field, holder }) => [field, holder]));
    const next = new Map(table);
    for (const field of new Set([...table.keys(), ...listed.keys()])) {
      if (!reaches(field, list.seq)) continue;
      const holder = listed.get(field);
      if (holder === undefined) next.delete(field);
      else next.set(field, holder);
    }
    listSeq = Math.max(listSeq, list.seq);
    if (!sameTable(table, next)) replace(next);
  }

  const renewer = setInterval(renewAll, RENEW_MS);

  return {
    getTable: () => table,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    lock(field) {
      wanted.add(field);
      return acquire('lock.acquire', field);
    },
    takeOver(field) {
      wanted.add(field);
      return acquire('lock.take_over', field);
    },
    release,
    applyChange({ field, holder, seq }) {
      if (seq <= seenFor(field)) return;
      fieldSeq.set(field, seq);
      setHolder(field, holder);
    },
    resync,
    onSubscribed() {
      subscribed = true;
      markSubscribed();
      void resync();
    },
    async releaseAll() {
      wanted.clear();
      // Only the locks this session holds now, released at once. An acquire still in flight is left
      // to the server: the last leave unsubscribes, which frees whatever it grants. A late release
      // here would free the lock a remounted session on this connection just took.
      const held = [...table.keys()].filter((field) => !pending.has(field) && isMine(table.get(field)));
      await Promise.all(held.map(release));
    },
    close() {
      clearInterval(renewer);
    },
  };
}
